import * as React from "react";
import { useRef, useState } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import { RouterViewTemplate, useLoadingState } from "../RouterView";
import { CalculationBasis, CashbackConfigV2, CashbackTypeV2, PlayerCashbackOverride, SetPlayerCashbackOverrideRequest } from "../../http/protocol";
import {
  blockedFractionMessage,
  blockingFractionLimit,
  CashbackFractionThresholds,
  isLargeFraction,
  isNewlyBlockingFraction,
  largeFractionThreshold,
  useCashbackFractionThresholds,
} from "./cashbackFractionThresholds";
import { Form, useForm } from "../form/Form";
import { getContainer } from "../../ioc/IOCSetup";
import { FormGroup } from "../form/FormGroup";
import { FormInput } from "../form/FormInput";
import { FormSubmitError, FormSubmitSuccess } from "../form/FormFeedback";
import { Submit } from "../form/Submit";
import { Validators } from "../form/Validators";
import { SelectInput } from "../form/SelectInput";
import { FeatureFlags, useFeatureFlag } from "../../feature-flags/FeatureFlagContext";
import Datetime from "react-datetime";
import moment from "moment";
import "react-datetime/css/react-datetime.css";
import { t } from "../../i18n";
import "./styles/ExpiryPicker.scss";

interface OverrideFormModel {
  userId?: number;
  configId?: number;
  fraction?: number;
  expiresAt?: number;
}

function toFormModel(override: PlayerCashbackOverride): OverrideFormModel {
  return {
    userId: override.userId,
    configId: override.configId,
    fraction: override.fraction,
    expiresAt: override.expiresAt ? new Date(override.expiresAt).getTime() : undefined,
  };
}

// react-datetime yields a moment when a valid date is picked and a string while the field is
// being typed or cleared; an empty field means a permanent override (no expiry).
function expiryFromPicker(value: moment.Moment | string): number | undefined {
  if (typeof value === "string") {
    return undefined;
  }
  return value.valueOf();
}

// A per-player override is hard-capped server-side at a fraction of 1.0, whereas the operator's
// cashback ceiling may sit higher. The override's client block therefore uses a ceiling clamped to
// 1.0 so it refuses at the same point the server would, and quotes that same clamped ceiling.
const OVERRIDE_MAX_FRACTION = 1;

function overrideBlockingThresholds(thresholds: CashbackFractionThresholds | undefined): CashbackFractionThresholds | undefined {
  if (!thresholds) {
    return undefined;
  }
  return {
    ...thresholds,
    blocking: Math.min(thresholds.blocking, OVERRIDE_MAX_FRACTION),
    rakeback: thresholds.rakeback && { ...thresholds.rakeback, blocking: Math.min(thresholds.rakeback.blocking, OVERRIDE_MAX_FRACTION) },
  };
}

function isPastDate(value?: number): boolean {
  if (value == null) {
    return false;
  }
  // The picker yields a full instant; an expiry already in the past is meaningless, so
  // compare against the current moment rather than the start of the day.
  return value < Date.now();
}

export const EditPlayerCashbackOverride = (): JSX.Element => {
  const history = useHistory();
  const match = useRouteMatch<{ userId?: string; configId?: string }>();
  const cashbackV2Enabled = useFeatureFlag(FeatureFlags.CASHBACK_V2_ENABLED);
  const service = getContainer().getPlayerCashbackOverrideService();
  const configService = getContainer().getCashbackConfigV2Service();
  const thresholds = useCashbackFractionThresholds();

  const isNew = match.params.userId == null || match.params.configId == null;

  // In edit mode the (userId, configId) key comes from the route and must not be changed.
  const lockUserId = !isNew;

  const [configs, setConfigs] = useState<CashbackConfigV2[]>([]);
  // The fraction as first loaded, so the blocking ceiling fires only when it is changed this session;
  // an already-stored over-ceiling override is grandfathered.
  const initialFraction = useRef<number | undefined>(undefined);
  const [blockedFraction, setBlockedFraction] = useState(false);

  const [form, formListener, setForm] = useForm<OverrideFormModel>({}, (): Promise<void> => {
    const model = form.model;

    setBlockedFraction(false);

    if (isPastDate(model.expiresAt)) {
      return Promise.reject(new Error(t("cashback.player-overrides.expires-in-past", undefined, "Expiry date cannot be in the past.")));
    }
    if (model.fraction != null && (model.fraction < 0 || model.fraction > 1)) {
      return Promise.reject(new Error(t("cashback.player-overrides.fraction-out-of-range", undefined, "Rate must be a factor between 0 and 1, e.g. 0.15.")));
    }

    const overrideType: CashbackTypeV2 | undefined = configs.find((config) => config.id === model.configId)?.type;
    const clampedThresholds = overrideBlockingThresholds(thresholds);
    if (isNewlyBlockingFraction(model.fraction, initialFraction.current, clampedThresholds, overrideType)) {
      setBlockedFraction(true);
      return Promise.reject(
        new Error(blockedFractionMessage("cashback.config-v2.large-fraction-blocked", blockingFractionLimit(clampedThresholds, overrideType))),
      );
    }

    const request: SetPlayerCashbackOverrideRequest = {
      userId: model.userId,
      configId: model.configId,
      fraction: model.fraction,
      // expiresAt travels as epoch millis on the wire; the protocol type is Date, so cast at the boundary.
      expiresAt: model.expiresAt as unknown as Date | undefined,
    };

    return service.save(request).then((saved: PlayerCashbackOverride) => {
      initialFraction.current = saved.fraction;
      setForm({ model: toFormModel(saved), validationMap: form.validationMap });
      // Return the operator near where they launched the edit (e.g. a player's card)
      // rather than stranding them on the cross-player list every time.
      if (history.length > 1) {
        history.goBack();
      } else {
        history.replace("/cashback/player-overrides/list");
      }
    });
  });

  const status = useLoadingState(async () => {
    const configResponse = await configService.list({ limit: 1000, offset: 0, orderBy: "id" });
    setConfigs(configResponse.items ?? []);

    if (isNew) {
      return;
    }

    const userId = Number(match.params.userId);
    const configId = Number(match.params.configId);
    const response = await service.listPaged({ userId, configId, includeExpired: true });
    const existing = (response.items ?? []).find((item) => item.userId === userId && item.configId === configId);
    if (existing) {
      initialFraction.current = existing.fraction;
      setForm({ model: toFormModel(existing), validationMap: {} });
    }
  }, [match.params.userId, match.params.configId]);

  if (!cashbackV2Enabled) {
    return <div />;
  }

  // An override inherits its parent config's basis and reward type, so its fraction is judged against
  // that config's threshold set; before a config is chosen, fall back to the stricter UNWEIGHTED basis.
  const overrideConfig = configs.find((config) => config.id === form.model.configId);
  const overrideBasis = overrideConfig?.calculationBasis ?? CalculationBasis.UNWEIGHTED;
  const overrideType = overrideConfig?.type;
  const showLargeFractionWarning = isLargeFraction(Number(form.model.fraction), overrideBasis, thresholds, overrideType);

  return (
    <RouterViewTemplate
      status={status}
      title={t("cashback.player-overrides.edit-title", undefined, "Player Cashback Override")}
    >
      <div className="wrapper wrapper-content animated fadeInRight">
        <Form
          className="form form-horizontal"
          validationMap={form.validationMap}
          modelObject={form.model}
          formListener={formListener}
        >
          <div className="row">
            <div className="col-lg-12">
              <div className="card-box">
                <div className="ibox-content">
                  <FormGroup className="form-group">
                    <label className="col-sm-3 control-label">{t("cashback.player-overrides.user-id", undefined, "User ID")}</label>
                    <div className="col-sm-10">
                      <FormInput
                        model="userId"
                        validators={[Validators.required(), Validators.integer()]}
                        type="integer"
                        className="form-control"
                        disabled={lockUserId}
                        data-testid="player-cashback-override-user-id"
                      />
                    </div>
                  </FormGroup>

                  <FormGroup className="form-group">
                    <label className="col-sm-3 control-label">{t("cashback.player-overrides.config-id", undefined, "Config")}</label>
                    <div className="col-sm-10">
                      <SelectInput
                        model="configId"
                        validators={[Validators.required()]}
                        className="form-control"
                        values={configs}
                        optionModelValue={(config: CashbackConfigV2) => config.id}
                        displayValue={(config: CashbackConfigV2) => config.name ?? String(config.id)}
                        disabled={!isNew}
                        data-testid="player-cashback-override-config-id"
                      />
                      <span className="help">{t("cashback.player-overrides.config-id-help", undefined, "Cashback config this override applies to.")}</span>
                    </div>
                  </FormGroup>

                  <FormGroup className="form-group">
                    <label className="col-sm-3 control-label">{t("cashback.player-overrides.rate", undefined, "Rate")}</label>
                    <div className="col-sm-10">
                      <FormInput
                        model="fraction"
                        validators={[Validators.required(), Validators.number(), Validators.range(0, 1)]}
                        type="float"
                        className="form-control"
                        data-testid="player-cashback-override-fraction"
                      />
                      <span className="help">{t("cashback.player-overrides.rate-help", undefined, "Rate as a factor between 0 and 1, e.g. 0.15.")}</span>
                      {thresholds && showLargeFractionWarning && (
                        <div
                          className="alert alert-warning mt-2 mb-0"
                          data-testid="player-cashback-override-large-fraction-warning"
                        >
                          <strong>{t("cashback.config-v2.large-fraction-warning-title", undefined, "Unusually large fraction")}</strong>{" "}
                          {t(
                            "cashback.config-v2.large-fraction-warning",
                            { threshold: largeFractionThreshold(overrideBasis, thresholds, overrideType), basis: overrideBasis },
                            "A fraction of {{threshold}} or more is unusually large for {{basis}} and may pay out far more than intended. This is advisory only — you can still save.",
                          )}
                        </div>
                      )}
                      {blockedFraction && (
                        <div
                          className="alert alert-danger mt-2 mb-0"
                          data-testid="player-cashback-override-large-fraction-blocked"
                        >
                          {blockedFractionMessage(
                            "cashback.config-v2.large-fraction-blocked",
                            blockingFractionLimit(overrideBlockingThresholds(thresholds), overrideType),
                          )}
                        </div>
                      )}
                    </div>
                  </FormGroup>

                  <FormGroup className="form-group">
                    <label className="col-sm-3 control-label">{t("cashback.player-overrides.expires", undefined, "Expires")}</label>
                    <div className="col-sm-10">
                      <div className="uco-expires uco-expires-form">
                        <Datetime
                          timeFormat="HH:mm"
                          dateFormat="YYYY-MM-DD"
                          value={form.model.expiresAt != null ? new Date(form.model.expiresAt) : ""}
                          onChange={(value) =>
                            setForm({
                              model: { ...form.model, expiresAt: expiryFromPicker(value) },
                              validationMap: form.validationMap,
                            })
                          }
                          inputProps={
                            {
                              className: "form-control",
                              "data-testid": "player-cashback-override-expires",
                            } as React.InputHTMLAttributes<HTMLInputElement>
                          }
                        />
                      </div>
                      <span className="help">{t("cashback.player-overrides.expires-help", undefined, "Leave empty for a permanent override.")}</span>
                    </div>
                  </FormGroup>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-12">
              <div className="card-box">
                {!blockedFraction && <FormSubmitError />}
                <FormSubmitSuccess text={t("cashback.player-overrides.save-success", undefined, "Override was saved successfully")} />
                <div className="form-group">
                  <div className="col-sm-12">
                    <Submit
                      className="btn btn-primary"
                      data-testid="player-cashback-override-save"
                    >
                      {t("cashback.player-overrides.save", undefined, "Save")}
                    </Submit>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Form>
      </div>
    </RouterViewTemplate>
  );
};

export default EditPlayerCashbackOverride;
